import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getAuthToken } from '../../services/api';
import './ParentSections.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const ParentSectionDashboard = () => {
  const { t } = useTranslation(['parent', 'common']);
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState({
    totalChildren: 0,
    totalEnrolledCourses: 0,
    totalCompletedCourses: 0,
    averageProgress: 0
  });

  // Get user from localStorage
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const parentId = user.id;

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!parentId) {
        setLoading(false);
        return;
      }

      try {
        const token = getAuthToken();
        
        // Fetch overview statistics
        const overviewResponse = await fetch(`${API_BASE_URL}/api/parent-dashboard/overview/${parentId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (overviewResponse.ok) {
          const overviewData = await overviewResponse.json();
          if (overviewData.success) {
            setOverview(overviewData.data);
          }
        }

        // Fetch children list
        const childrenResponse = await fetch(`${API_BASE_URL}/api/parent-dashboard/children/${parentId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (childrenResponse.ok) {
          const childrenData = await childrenResponse.json();
          if (childrenData.success) {
            setChildren(childrenData.data);
          }
        } else {
          // If API fails, use mock data as fallback
          console.warn('Failed to fetch children, using mock data');
          setChildren([
            {
              id: 1,
              name: 'Emma Smith',
              age: 12,
              grade: '6th Grade',
              enrolledCourses: 5,
              completedCourses: 2,
              progress: 65,
              avatar: 'https://via.placeholder.com/80/667eea/ffffff?text=ES'
            },
            {
              id: 2,
              name: 'James Smith',
              age: 10,
              grade: '4th Grade',
              enrolledCourses: 3,
              completedCourses: 1,
              progress: 45,
              avatar: 'https://via.placeholder.com/80/667eea/ffffff?text=JS'
            }
          ]);
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        // Use mock data as fallback
        setChildren([
          {
            id: 1,
            name: 'Emma Smith',
            age: 12,
            grade: '6th Grade',
            enrolledCourses: 5,
            completedCourses: 2,
            progress: 65,
            avatar: 'https://via.placeholder.com/80/667eea/ffffff?text=ES'
          },
          {
            id: 2,
            name: 'James Smith',
            age: 10,
            grade: '4th Grade',
            enrolledCourses: 3,
            completedCourses: 1,
            progress: 45,
            avatar: 'https://via.placeholder.com/80/667eea/ffffff?text=JS'
          }
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [parentId]);

  // Use overview data from API if available, otherwise calculate from children
  const totalChildren = overview.totalChildren || children.length;
  const totalEnrolledCourses = overview.totalEnrolledCourses || children.reduce((sum, child) => sum + (child.enrolledCourses || 0), 0);
  const totalCompletedCourses = overview.totalCompletedCourses || children.reduce((sum, child) => sum + (child.completedCourses || 0), 0);
  const averageProgress = overview.averageProgress || (children.length > 0 
    ? Math.round(children.reduce((sum, child) => sum + (child.progress || 0), 0) / children.length)
    : 0);

  if (loading) {
    return (
      <div className="parent-main-content">
        <div className="parent-content-header">
          <h1 className="parent-content-title">{t('dashboard.title')}</h1>
          <p className="parent-content-subtitle">{t('dashboard.subtitle')}</p>
        </div>
        <div className="parent-loading">
          <p>{t('messages.loading', { ns: 'common' })}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="parent-main-content">
      <div className="parent-content-header">
        <h1 className="parent-content-title">{t('dashboard.title')}</h1>
        <p className="parent-content-subtitle">{t('dashboard.subtitle')}</p>
      </div>

      {/* Overview Statistics */}
      <div className="parent-stats-grid">
        <div className="parent-stat-card">
          <div className="parent-stat-icon">
            <i className="material-icons">people</i>
          </div>
          <div className="parent-stat-content">
            <h3 className="parent-stat-value">{totalChildren}</h3>
            <p className="parent-stat-label">{t('dashboard.stats.children')}</p>
          </div>
        </div>

        <div className="parent-stat-card">
          <div className="parent-stat-icon">
            <i className="material-icons">school</i>
          </div>
          <div className="parent-stat-content">
            <h3 className="parent-stat-value">{totalEnrolledCourses}</h3>
            <p className="parent-stat-label">{t('dashboard.stats.enrolledCourses')}</p>
          </div>
        </div>

        <div className="parent-stat-card">
          <div className="parent-stat-icon">
            <i className="material-icons">check_circle</i>
          </div>
          <div className="parent-stat-content">
            <h3 className="parent-stat-value">{totalCompletedCourses}</h3>
            <p className="parent-stat-label">{t('dashboard.stats.completedCourses')}</p>
          </div>
        </div>

        <div className="parent-stat-card">
          <div className="parent-stat-icon">
            <i className="material-icons">trending_up</i>
          </div>
          <div className="parent-stat-content">
            <h3 className="parent-stat-value">{averageProgress}%</h3>
            <p className="parent-stat-label">{t('dashboard.stats.averageProgress')}</p>
          </div>
        </div>
      </div>

      {/* Children Overview */}
      <div className="parent-section">
        <div className="parent-section-header">
          <h2 className="parent-section-title">{t('dashboard.childrenOverview')}</h2>
        </div>

        {children.length === 0 ? (
          <div className="parent-empty-state">
            <i className="material-icons">people_outline</i>
            <p>{t('dashboard.noChildren')}</p>
            <button className="parent-btn primary">
              <i className="material-icons">add</i>
              {t('dashboard.addFirstChild')}
            </button>
          </div>
        ) : (
          <div className="parent-children-grid">
            {children.map((child) => (
              <div key={child.id} className="parent-child-card">
                <div className="parent-child-header">
                  <div className="parent-child-avatar">
                    <img src={child.avatar} alt={child.name} />
                  </div>
                  <div className="parent-child-info">
                    <h3 className="parent-child-name">{child.name}</h3>
                    <p className="parent-child-details">
                      {child.age} {t('dashboard.yearsOld')} • {child.grade}
                    </p>
                  </div>
                </div>

                <div className="parent-child-progress">
                  <div className="parent-progress-header">
                    <span className="parent-progress-label">{t('dashboard.overallProgress')}</span>
                    <span className="parent-progress-value">{child.progress}%</span>
                  </div>
                  <div className="parent-progress-bar">
                    <div 
                      className="parent-progress-fill" 
                      style={{ width: `${child.progress}%` }}
                    ></div>
                  </div>
                </div>

                <div className="parent-child-stats">
                  <div className="parent-child-stat">
                    <i className="material-icons">school</i>
                    <span>{child.enrolledCourses} {t('dashboard.courses')}</span>
                  </div>
                  <div className="parent-child-stat">
                    <i className="material-icons">check_circle</i>
                    <span>{child.completedCourses} {t('dashboard.completed')}</span>
                  </div>
                </div>

                <button className="parent-btn secondary">
                  {t('dashboard.viewDetails')}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent Activity */}
      <div className="parent-section">
        <div className="parent-section-header">
          <h2 className="parent-section-title">{t('dashboard.recentActivity')}</h2>
        </div>

        <div className="parent-activity-list">
          <div className="parent-activity-item">
            <div className="parent-activity-icon">
              <i className="material-icons">check_circle</i>
            </div>
            <div className="parent-activity-content">
              <p className="parent-activity-text">
                <strong>Emma Smith</strong> {t('dashboard.completedCourse')} "English Basics"
              </p>
              <p className="parent-activity-time">2 hours ago</p>
            </div>
          </div>

          <div className="parent-activity-item">
            <div className="parent-activity-icon">
              <i className="material-icons">school</i>
            </div>
            <div className="parent-activity-content">
              <p className="parent-activity-text">
                <strong>James Smith</strong> {t('dashboard.startedCourse')} "Vocabulary Builder"
              </p>
              <p className="parent-activity-time">1 day ago</p>
            </div>
          </div>

          <div className="parent-activity-item">
            <div className="parent-activity-icon">
              <i className="material-icons">trending_up</i>
            </div>
            <div className="parent-activity-content">
              <p className="parent-activity-text">
                <strong>Emma Smith</strong> {t('dashboard.achievedMilestone')} "100 Words Learned"
              </p>
              <p className="parent-activity-time">3 days ago</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ParentSectionDashboard;

